import {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react'
import _ from 'lodash'
import {Dimmer, Loader} from 'semantic-ui-react'
import {AuthContext, RequirePermission} from '@/features/auth'
import {MessageContext} from '@/features/errors'
import {
  DataSourceTemplates,
  IDataSource,
  IDataSourceItem,
  IDataSourceItemMap,
  IDataSourceStats,
  LegacyCatalogContextWrap,
  useDataSourceStats,
} from '..'
import {DataSourceOverrideDialog} from '../components/override-dialog'
export * from './legacy'

export interface IDataSourcesContextWrapperProps {
  children: React.ReactElement
}

export interface IDataSourcesContext {
  isBusy: boolean
  isDialogOpen: boolean
  mergedItems: IDataSourceItem[]
  mergedItemsStats: IDataSourceStats
  sourceConfigs: IDataSource[]
  sourceData: DataSourceResultsMap
  activeCatalogName: string
  replaceSources: (sources: IDataSource[]) => void
  addSource: (source: IDataSource) => void
  showDialog: () => void
  skipLoadingSources: () => void
  findItem: (id: string, includeUpstream?: boolean) => Promise<IDataSourceItem | undefined>
  /** @deprecated */
  ensureFetch: () => void
}

export type DataSourceResultsMap = {
  [key in keyof typeof DataSourceTemplates]: {
    [key: string]: IDataSourceItemMap
  }
}
type mergedScoreMap = {
  [from: string]: {
    ask: {
      [to: string]: number
    }
    similar: {
      [to: string]: number
    }
  }
}

const defaultSourceResultsMap: DataSourceResultsMap = {
  catalog: {},
  localFile: {},
  similar: {},
  ask: {},
  expression: {},
  gfpItem: {},
}

export const DataSourcesContext = createContext<IDataSourcesContext>({
  isBusy: false,
  isDialogOpen: false,
  mergedItems: [],
  mergedItemsStats: {
    categories: {},
    fields: {},
    fieldTypes: {},
    types: {},
    numbers: {},
  },
  sourceConfigs: [],
  sourceData: defaultSourceResultsMap,
  activeCatalogName: '',
  replaceSources: () => {
    throw new Error('not implemented')
  },
  addSource: () => {
    throw new Error('not implemented')
  },
  showDialog: () => {
    throw new Error('not implemented')
  },
  skipLoadingSources: () => {
    throw new Error('not implemented')
  },
  findItem: () => {
    throw new Error('not implemented')
  },
  ensureFetch: () => {
    throw new Error('not implemented')
  },
})

export function DataSourcesContextWrapper(props: IDataSourcesContextWrapperProps) {
  const {children} = props

  const {default_catalog, havePermission} = useContext(AuthContext)

  const {logError} = useContext(MessageContext)

  const [isBusy, setIsBusyState] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(
    havePermission('auth_utils.access_to_all_catalogs')
  )
  const [activeCatalogName, setActiveCatalogName] = useState(default_catalog)
  const [sourceConfigs, setSourceConfigs] = useState<IDataSource[]>([])
  const [sourceData, setSourceData] = useState<DataSourceResultsMap>(defaultSourceResultsMap)
  const [mergedItems, setMergedItems] = useState<IDataSourceItem[]>([])
  const [skipIsBusy, setSkipIsBusy] = useState(false)

  const findItemCache = useRef<{
    [key: string]: {
      item: IDataSourceItem | undefined
      expires: number
    }
  }>({})

  const setIsBusy = useCallback(
    (busy: boolean) => {
      if (skipIsBusy) return
      setIsBusyState(busy)
    },
    [skipIsBusy]
  )

  useEffect(() => {
    if (!(sourceConfigs && sourceConfigs.length > 0)) {
      setSourceData(defaultSourceResultsMap)
      return
    }
    async function f() {
      setIsBusy(true)
      const res: DataSourceResultsMap = {
        ...defaultSourceResultsMap,
      }
      for (let i = 0; i < sourceConfigs.length; i++) {
        const {sourceID, key} = sourceConfigs[i]
        if (sourceData[sourceID][key]) {
          continue
        }
        try {
          const data = await fetchSourceConfig(sourceConfigs[i])
          if (!data) continue
          res[sourceID][key] = data
        } catch (error) {
          logError('Failed to fetch source', error)
        }
      }
      setSourceData(res)
      setIsBusy(false)
    }
    f()
  }, [sourceConfigs])

  useEffect(() => {
    function createMergedItem(id: string, scores: mergedScoreMap): IDataSourceItem | undefined {
      const _sourceItem = _.chain(sourceConfigs)
        .filter((src) => src.sourceID === 'catalog' || src.sourceID === 'localFile')
        .map(({sourceID, key}) => _.cloneDeep(_.get(sourceData[sourceID][key], id)))
        .reduce(_.merge)
        .value()
      if (!_sourceItem) return
      if (scores[id]) _sourceItem._merged = scores[id]
      return _sourceItem
    }

    const _allIDs = _.chain(sourceConfigs)
      .map(({sourceID, key}) => sourceData[sourceID][key])
      .flatMap(_.keys)
      .uniq()
      .value()

    /**
     * prepare scores for merging
     */
    const _mergeScores: mergedScoreMap = {}
    _.chain(sourceConfigs)
      .forEach(({sourceID, key}) => {
        if (sourceID === 'similar') {
          const _data = sourceData[sourceID][key]
          if (!_data) return
          _.chain(_data)
            .toPairs()
            .forEach(([k, v]) => {
              const _score = _.get(v, 'score')
              if (_score === undefined) return
              _.set(_mergeScores, `["${k}"]["similar"]["${key}"]`, _score)
              _.set(_mergeScores, `["${key}"]["similar"]["${k}"]`, _score)
            })
            .value()
        } else if (sourceID === 'ask') {
          const _data = sourceData[sourceID][key]
          if (!_data) return
          _.chain(_data)
            .toPairs()
            .forEach(([k, v]) => {
              const _score = _.get(v, 'score')
              if (_score === undefined) return
              _.set(_mergeScores, `["${k}"]["ask"]["${key}"]`, _score)
              // _.set(_mergeScores, `["${key}"]["ask"]["${k}"]`, _score);
            })
            .value()
        } else if (sourceID === 'expression') {
          const _data = sourceData[sourceID][key]
          if (!_data) return
          _.chain(_data)
            .toPairs()
            .forEach(([k, v]) => {
              if (v === undefined) return
              _.set(_mergeScores, `["${k}"]["expression"]["${key}"]`, v)
              // _.set(_mergeScores, `["${key}"]["ask"]["${k}"]`, _score);
            })
            .value()
        }
      })
      .value()

    const _merged = _.chain(_allIDs)
      .map((id) => createMergedItem(id, _mergeScores))
      .compact()
      .value()
    const _catalogName: string | undefined = _.chain(_merged)
      .slice(0, 100)
      .map((item) => {
        return item.catalog
      })
      .compact()
      .first()
      .value()

    setActiveCatalogName(_catalogName || default_catalog)
    setMergedItems(_merged)
  }, [sourceData])

  const mergedItemsStats = useDataSourceStats(mergedItems)

  function replaceSources(sources: IDataSource[]) {
    setSourceConfigs(sources)
  }

  function addSource(source: IDataSource) {
    setSourceConfigs((prev) => {
      const exists = _.chain(prev)
        .map((s) => s.sourceID === source.sourceID && _.isEqual(s.args, source.args))
        .reduce((a, b) => a || b)
        .value()
      if (exists) {
        return prev
      }
      return [...prev, source]
    })
  }

  function showDialog() {
    setIsDialogOpen(true)
  }

  function skipLoadingSources() {
    setSkipIsBusy(true)
    setIsDialogOpen(false)
    setIsBusyState(false)
  }

  async function fetchSourceConfig(
    config: IDataSource,
    ignoreCache: boolean = false
  ): ReturnType<IDataSource['fetch']> {
    const {sourceID, key, fetch} = config
    if (!ignoreCache && sourceData[sourceID][key]) {
      return sourceData[sourceID][key]
    }
    try {
      return await fetch()
    } catch (error) {
      logError('Failed to fetch source', error)
    }
  }

  function ensureFetch() {
    if (isDialogOpen) return
    if (mergedItems && mergedItems.length > 0) return
    if (isBusy) return
    addSource(
      DataSourceTemplates.catalog({
        catalogName: default_catalog,
      })
    )
  }

  async function findItem(
    id: string,
    includeUpstream = true
  ): Promise<IDataSourceItem | undefined> {
    if (isDialogOpen) return
    const cacheTTL = 10 * 60 * 1000
    const _cachedItem = findItemCache.current[id]
    if (_cachedItem) {
      if (_cachedItem.expires >= Date.now()) return _cachedItem.item || undefined
      delete findItemCache.current[id]
    }
    let _item = _.find(mergedItems, (item) => item._id === id)
    if (_item) {
      findItemCache.current[id] = {
        expires: Date.now() + cacheTTL,
        item: _item,
      }
      return _item
    }
    if (!includeUpstream) return _item
    // FETCH UPSTREAM
    _item = _.get(
      await fetchSourceConfig(
        DataSourceTemplates.gfpItem({
          catalog: activeCatalogName,
          vionlabsID: id,
        })
      ),
      id
    )
    findItemCache.current[id] = {
      expires: Date.now() + cacheTTL,
      item: _item,
    }
    return _item
  }

  return (
    <DataSourcesContext.Provider
      value={{
        isBusy,
        isDialogOpen,
        mergedItems,
        mergedItemsStats,
        sourceConfigs,
        sourceData,
        activeCatalogName,
        addSource,
        replaceSources,
        findItem,
        showDialog,
        skipLoadingSources,
        ensureFetch,
      }}
    >
      <RequirePermission require={'auth_utils.access_to_all_catalogs'}>
        <DataSourceOverrideDialog onClose={() => setIsDialogOpen(false)} />
      </RequirePermission>
      <LegacyCatalogContextWrap>{children}</LegacyCatalogContextWrap>
      <Dimmer active={isBusy}>
        <Loader />
      </Dimmer>
    </DataSourcesContext.Provider>
  )
}
